import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { Factory, Package, CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProductionPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/production");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <p className="text-sm text-zinc-500">{t("common.loading")}</p>;

  const items = data?.items || [];
  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US", {
      month: "short", day: "numeric", year: "numeric",
    }) : "—";

  return (
    <div className="space-y-8" data-testid="production-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-3">
          <Factory size={30} weight="bold" className="text-[#8a6a1c]" />
          {t("production.page_title")}
        </h1>
        <p className="text-sm sm:text-base text-zinc-600 mt-1">{t("production.page_subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white border-2 border-black rounded-xl p-5 shadow-sm" data-testid="production-total-units">
          <div className="flex items-center gap-2 text-zinc-500 text-sm font-medium">
            <Package size={16} weight="bold" /> {t("production.total_units")}
          </div>
          <p className="text-4xl font-semibold mt-2">{data?.total_units ?? 0}</p>
        </div>
        <div className="bg-[#fbf6e7] border-2 border-[#c9a24a] rounded-xl p-5 shadow-sm" data-testid="production-total-cogs">
          <div className="flex items-center gap-2 text-[#8a6a1c] text-sm font-semibold">
            <CurrencyDollar size={16} weight="bold" /> {t("production.total_cogs")}
          </div>
          <p className="text-4xl font-semibold mt-2 text-[#8a6a1c]">
            ${(data?.total_cost ?? 0).toFixed(2)}
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-5 text-sm text-zinc-600">
          {t("production.empty")}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const unitCost = Number(item.wholesale_price) || 0;
            const lineCost = (unitCost * (item.quantity || 0)).toFixed(2);
            return (
              <div
                key={item.id}
                className="bg-white border border-zinc-200 rounded-xl p-4 sm:p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`production-row-${item.id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-[#fbf6e7] border border-[#c9a24a] text-[#8a6a1c] flex items-center justify-center shrink-0">
                  <Factory size={16} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-base truncate">
                    {item.quantity} × {item.product_name}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {fmtDate(item.created_at)} · {t("production.logged_by", { name: item.from_user_name || "" })}
                    {item.note ? ` · ${item.note}` : ""}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {unitCost > 0 ? (
                    <>
                      <p className="font-semibold text-lg text-[#8a6a1c]">${lineCost}</p>
                      <p className="text-xs text-zinc-500">
                        {t("production.unit_cost", { cost: unitCost.toFixed(2) })}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-zinc-400">{t("production.no_cost")}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
